(() => {
  if (window.HoyostarDiscountConfetti) return;

  const COLORS = [
    '#6d388b', '#6faed9', '#ef4aa8', '#f7c948',
    '#7b4fd6', '#39c7a5', '#ff7a45', '#ffffff'
  ];
  const PROGRESS_DURATION = 720;
  const PROGRESS_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

  const state = {
    frame: null,
    lastTarget: null,
    lastLaunchAt: 0,
    cartBars: new Map(),
    progressAnimations: new WeakMap()
  };

  const random = (min, max) => Math.random() * (max - min) + min;

  const getCanvas = () => {
    let canvas = document.querySelector('[data-discount-confetti-canvas]');
    if (canvas) return canvas;

    canvas = document.createElement('canvas');
    canvas.setAttribute('data-discount-confetti-canvas', '');
    Object.assign(canvas.style, {
      position: 'fixed',
      inset: '0',
      width: '100vw',
      height: '100vh',
      pointerEvents: 'none',
      zIndex: '999999'
    });
    document.body.appendChild(canvas);

    return canvas;
  };

  const prepareCanvas = (canvas) => {
    const dpr = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;

    const context = canvas.getContext('2d');
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    return context;
  };

  const animateRewardIcon = (target) => {
    if (typeof target.animate !== 'function') return;

    target.getAnimations?.().forEach((animation) => animation.cancel());
    target.animate(
      [
        { opacity: 0, transform: 'translateY(4px) scale(0.72) rotate(-8deg)' },
        { opacity: 1, transform: 'translateY(-1px) scale(1.1) rotate(3deg)', offset: 0.7 },
        { opacity: 1, transform: 'translateY(0) scale(1) rotate(0)' }
      ],
      {
        duration: 520,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
      }
    );
  };

  const launch = (target) => {
    if (!(target instanceof Element)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const now = performance.now();
    if (state.lastTarget === target && now - state.lastLaunchAt < 900) return;

    const rect = target.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    animateRewardIcon(target);

    state.lastTarget = target;
    state.lastLaunchAt = now;

    const mobile = window.innerWidth < 750;
    const canvas = getCanvas();
    const context = prepareCanvas(canvas);
    const originX = Math.min(
      Math.max(rect.left + rect.width / 2, 36),
      window.innerWidth - 36
    );
    const originY = rect.top + rect.height / 2;
    const duration = mobile ? 1450 : 1750;
    const count = mobile ? 46 : 72;
    const startedAt = performance.now();

    const particles = Array.from({ length: count }, () => {
      const angle = random(mobile ? -145 : -165, mobile ? -35 : -15) * Math.PI / 180;
      const speed = random(mobile ? 5.2 : 6.5, mobile ? 9.5 : 13.5);
      const shapeRoll = Math.random();

      return {
        x: originX + random(-12, 12),
        y: originY + random(-7, 7),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        gravity: random(0.11, 0.19),
        drag: random(0.978, 0.992),
        width: random(2.5, mobile ? 4.8 : 5.8),
        height: random(5, mobile ? 10 : 12),
        ribbonWidth: random(1.7, mobile ? 2.8 : 3.4),
        ribbonLength: random(mobile ? 18 : 21, mobile ? 29 : 35),
        rotation: random(0, Math.PI * 2),
        rotationSpeed: random(-0.24, 0.24),
        wave: random(0, Math.PI * 2),
        waveSpeed: random(0.13, 0.24),
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        shape: shapeRoll < 0.16
          ? 'ribbon'
          : shapeRoll < 0.34
            ? 'circle'
            : shapeRoll < 0.48
              ? 'diamond'
              : 'rect'
      };
    });

    if (state.frame) cancelAnimationFrame(state.frame);

    const draw = (time) => {
      const progress = Math.min((time - startedAt) / duration, 1);
      context.clearRect(0, 0, window.innerWidth, window.innerHeight);

      particles.forEach((particle) => {
        particle.vx *= particle.drag;
        particle.vy = particle.vy * particle.drag + particle.gravity;
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.rotation += particle.rotationSpeed;
        particle.wave += particle.waveSpeed;

        context.save();
        const fade = Math.max(0, (progress - 0.58) / 0.42);
        context.globalAlpha = 1 - fade * fade;
        context.translate(particle.x, particle.y);
        context.rotate(particle.rotation);

        if (particle.shape === 'ribbon') {
          context.strokeStyle = particle.color;
          context.lineWidth = particle.ribbonWidth;
          context.lineCap = 'round';
          context.beginPath();
          context.moveTo(0, -particle.ribbonLength / 2);
          context.bezierCurveTo(
            Math.sin(particle.wave) * particle.ribbonLength * 0.3,
            -particle.ribbonLength * 0.18,
            -Math.sin(particle.wave) * particle.ribbonLength * 0.3,
            particle.ribbonLength * 0.18,
            0,
            particle.ribbonLength / 2
          );
          context.stroke();
        } else if (particle.shape === 'circle') {
          context.fillStyle = particle.color;
          context.beginPath();
          context.arc(0, 0, particle.width * 0.7, 0, Math.PI * 2);
          context.fill();
        } else if (particle.shape === 'diamond') {
          context.fillStyle = particle.color;
          context.beginPath();
          context.moveTo(0, -particle.height / 2);
          context.lineTo(particle.width / 2, 0);
          context.lineTo(0, particle.height / 2);
          context.lineTo(-particle.width / 2, 0);
          context.closePath();
          context.fill();
        } else {
          context.fillStyle = particle.color;
          context.fillRect(
            -particle.width / 2,
            -particle.height / 2,
            particle.width,
            particle.height
          );
        }

        context.restore();
      });

      if (progress < 1) {
        state.frame = requestAnimationFrame(draw);
      } else {
        context.clearRect(0, 0, window.innerWidth, window.innerHeight);
        state.frame = null;
      }
    };

    state.frame = requestAnimationFrame(draw);
  };

  const cartBarKey = (root, index) => {
    const sectionId = root.closest('.shopify-section')?.id || 'cart';
    return `${sectionId}:${index}`;
  };

  const animateCartProgress = (bar, from, to) => {
    bar.style.setProperty('--discount-progress-width', `${to}%`);

    if (
      from === to ||
      typeof bar.animate !== 'function' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    state.progressAnimations.get(bar)?.cancel();

    const animation = bar.animate(
      [
        { width: `${from}%` },
        { width: `${to}%` }
      ],
      {
        duration: PROGRESS_DURATION,
        easing: PROGRESS_EASING,
        fill: 'both'
      }
    );

    state.progressAnimations.set(bar, animation);
    animation.addEventListener('finish', () => {
      animation.cancel();
      state.progressAnimations.delete(bar);
    }, { once: true });
  };

  const syncCartBars = (animate = false) => {
    document.querySelectorAll('.free-shipping-bar[data-unlocked-tier]').forEach((root, index) => {
      const key = cartBarKey(root, index);
      const bar = root.querySelector('[data-progress-bar]');
      const current = {
        tier: Number(root.dataset.unlockedTier || 0),
        progress: Number(root.dataset.progress || 0)
      };
      const previous = state.cartBars.get(key);

      state.cartBars.set(key, current);
      if (!bar || !animate || !previous) return;

      if (current.progress !== previous.progress) {
        animateCartProgress(bar, previous.progress, current.progress);
      }

      if (current.tier > previous.tier) {
        launch(root.querySelector('.discount-confetti'));
      }
    });
  };

  window.HoyostarDiscountConfetti = { launch };

  document.addEventListener('discount-progress:unlocked', (event) => {
    launch(event.detail?.target);
  });

  document.addEventListener('cart:updated', () => {
    syncCartBars(true);
  });

  document.addEventListener('shopify:section:load', () => {
    requestAnimationFrame(() => syncCartBars(false));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncCartBars(false), { once: true });
  } else {
    syncCartBars(false);
  }
})();
